/**
 * XION Medical product data — Endoscopes (flexible, fibre-optic, rigid)
 * Content sourced from xion-medical.com (split from completeProductsData.js).
 */

export const flexibleEndoscopes = {
  id: 'flexible-video-endoscopes',
  name: 'Flexible Video Endoscopes',
  slug: 'flexible-video-endoscopes',
  category: 'Endoscopes',
  description: 'XION manufactures flexible video nasopharyngoscopes for different processor platforms. Various application parts with different sensor resolutions and shaft diameters are available for the current Spectar and Veezar camera platforms. Versions with a working channel provide the means for sampling and minor therapeutic interventions.',
  overview: 'Highest image quality and convenient handling: image sensor, light source, optics, microphone and control keys are integrated in a single instrument and connected to the Spectar Camera Processor by means of just a single cable. The ergonomically shaped handle features a soft-touch design and the instruments are STERRAD™ compatible — suitable for plasma sterilisation with hydrogen peroxide.',
  products: [
    {
      id: 'spectar-video-nasopharyngoscopes',
      name: 'Spectar Video Nasopharyngoscopes',
      slug: 'spectar-video-nasopharyngoscopes',
      tagline: 'XN-series flexible video nasopharyngoscopes for Spectar systems',
      description: 'The XN-series Spectar video nasopharyngoscopes are used in EndoSTROBE, EndoSURGERY and EndoOFFICE Spectar systems. Image sensor, light source, optics, microphone and control keys are integrated in a single instrument and connected to the Spectar Camera Processor by a single cable. Versions with a working channel allow sampling and minor therapeutic interventions.',
      features: [
        'Highest image quality and convenient handling',
        'Sensor, light source, optics, microphone and keys in one instrument — single-cable connection',
        'Ergonomically shaped handle with soft-touch design',
        'STERRAD™ compatible — plasma sterilisation with hydrogen peroxide',
        'Available with or without working channel for sampling / minor therapy',
        'For EndoSTROBE, EndoSURGERY and EndoOFFICE Spectar systems'
      ],
      specifications: {
        series: 'XN series',
        platform: 'Spectar Camera Processors',
        sterilisation: 'STERRAD™ (plasma / H₂O₂)'
      },
      images: ['/products/transparent/spectar-video-nasopharyngoskop-sterrad-web.png']
    },
    {
      id: 'veezar-video-nasopharyngoscopes',
      name: 'Veezar Video Nasopharyngoscopes',
      slug: 'veezar-video-nasopharyngoscopes',
      tagline: 'FN-series flexible video nasopharyngoscopes for Veezar systems',
      description: 'The FN-series Veezar video nasopharyngoscopes are used in EndoCOMPACT Veezar and EndoPORTABLE Veezar systems. With integrated sensor, illumination and optics in a single instrument, they deliver excellent image quality and convenient handling for ENT diagnostics and FEES swallowing examinations.',
      features: [
        'Highest image quality and convenient handling',
        'Integrated sensor, light source and optics in a single instrument',
        'Ergonomically shaped handle with soft-touch design',
        'STERRAD™ compatible — plasma sterilisation with hydrogen peroxide',
        'Available with or without working channel',
        'For EndoCOMPACT Veezar and EndoPORTABLE Veezar systems (FN10 / FN20)'
      ],
      specifications: {
        series: 'FN series (FN10 / FN20)',
        platform: 'Veezar Camera Platform',
        sterilisation: 'STERRAD™ (plasma / H₂O₂)'
      },
      images: ['/products/transparent/veezar-video-nasopharyngoskop-sterrad-web.png']
    }
  ]
};

export const fibreOpticEndoscopes = {
  id: 'flexible-fibre-optic-endoscopes',
  name: 'Flexible Fibre-Optic Endoscopes',
  slug: 'flexible-fibre-optic-endoscopes',
  category: 'Endoscopes',
  description: 'XION fibre-optic nasopharyngoscopes have for many years proven successful in routine use worldwide. Rugged, easy to handle and of high optical quality, they are the dependable choice for everyday ENT endoscopy.',
  overview: 'In addition to the standard EF-N instrument, a paediatric endoscope (EF-N slim) and the EF-N 14 with a 1.4 mm working channel are available. All instruments are STERRAD™ compatible — suitable for plasma sterilisation with hydrogen peroxide (except the nasopharyngoscope with working channel).',
  products: [
    {
      id: 'ef-n',
      name: 'EF-N Fibre-Optic Nasopharyngoscope',
      slug: 'ef-n',
      tagline: 'The proven standard fibre-optic nasopharyngoscope',
      description: 'The standard EF-N fibre-optic nasopharyngoscope has proven successful in routine use worldwide for many years. A rugged device with easy handling and high optical quality for everyday ENT diagnostics.',
      features: [
        'Rugged device for routine use',
        'Easy handling',
        'High optical quality',
        'STERRAD™ compatible — plasma sterilisation with hydrogen peroxide'
      ],
      specifications: {
        type: 'Fibre-optic nasopharyngoscope',
        model: 'EF-N (standard)',
        sterilisation: 'STERRAD™ (plasma / H₂O₂)'
      },
      images: ['/products/transparent/faser-nasopharyngoskop-web.png']
    },
    {
      id: 'ef-n-slim',
      name: 'EF-N slim (Paediatric)',
      slug: 'ef-n-slim',
      tagline: 'Slim paediatric fibre-optic nasopharyngoscope',
      description: 'The EF-N slim is a paediatric fibre-optic nasopharyngoscope with a reduced shaft diameter for gentle examinations of younger patients, while retaining the rugged build and high optical quality of the EF-N range.',
      features: [
        'Slim shaft for paediatric examinations',
        'Rugged device for routine use',
        'Easy handling and high optical quality',
        'STERRAD™ compatible — plasma sterilisation with hydrogen peroxide'
      ],
      specifications: {
        type: 'Fibre-optic nasopharyngoscope',
        model: 'EF-N slim (paediatric)',
        sterilisation: 'STERRAD™ (plasma / H₂O₂)'
      },
      images: ['/products/transparent/faser-nasopharyngoskop-web.png']
    },
    {
      id: 'ef-n-14',
      name: 'EF-N 14 (with Working Channel)',
      slug: 'ef-n-14',
      tagline: 'Fibre-optic nasopharyngoscope with 1.4 mm working channel',
      description: 'The EF-N 14 fibre-optic nasopharyngoscope features a 1.4 mm working channel for sampling and minor therapeutic interventions during ENT endoscopy, combining the proven EF-N optics with interventional capability.',
      features: [
        '1.4 mm working channel for sampling / minor therapy',
        'Rugged device for routine use',
        'Easy handling and high optical quality',
        'Robust construction for everyday interventional use'
      ],
      specifications: {
        type: 'Fibre-optic nasopharyngoscope',
        model: 'EF-N 14',
        workingChannel: '1.4 mm'
      },
      images: ['/products/transparent/faser-nasopharyngoskop-web.png']
    }
  ]
};

export const rigidEndoscopes = {
  id: 'rigid-endoscopes',
  name: 'Rigid Endoscopes',
  slug: 'rigid-endoscopes',
  category: 'Endoscopes',
  description: 'XION offers a wide range of rigid endoscopes in diameters from 2.7 to 10 mm, with various working lengths and viewing angles adapted to the respective application. High-end optical systems and the quality of the fibre-optic illumination system guarantee sharp, high-contrast images and thus support reliable diagnostics.',
  overview: 'XION endoscopes are well-known for their ergonomics, safe handling, ruggedness and resilience to all common reprocessing procedures. The portfolio spans otoscopes, sinuscopes, laryngoscopes, laparoscopes and hysteroscopes, as well as 3D endoscope attachments for the Spectar 3D camera system.',
  products: [
    {
      id: 'otoscopes',
      name: 'Otoscopes',
      slug: 'otoscopes',
      tagline: 'Rigid otoscopes for ear examination',
      description: 'XION otoscopes are available in diameters from 2.7 mm to 4 mm and in different working lengths and viewing directions. High-end optics and high-quality fibre-optic illumination deliver sharp, high-contrast images for reliable otological diagnostics.',
      features: [
        'Diameters from 2.7 mm to 4 mm',
        'Different working lengths and viewing directions',
        'High-end optical system with high-contrast imaging',
        'Rugged and ergonomic for routine use',
        'Resilient to all common reprocessing procedures'
      ],
      specifications: {
        type: 'Rigid otoscope',
        diameters: '2.7 – 4 mm',
        configurations: 'Various working lengths & viewing directions'
      },
      images: ['/products/transparent/otoskop-2-7-mm-0-web.png']
    },
    {
      id: 'sinuscopes',
      name: 'Sinuscopes',
      slug: 'sinuscopes',
      tagline: 'Rigid sinuscopes for nasal & sinus endoscopy',
      description: 'XION sinuscopes are available in diameters of 2.7 mm, 3 mm and 4 mm and in different working lengths and viewing directions. As with all XION endoscopes, they offer excellent image quality with high ruggedness and resilience to aggressive sterilisation procedures.',
      features: [
        'Diameters of 2.7 mm, 3 mm and 4 mm',
        'Different working lengths and viewing directions',
        'Excellent image quality for sinus diagnostics and FESS',
        'High ruggedness and durability',
        'Resilient to aggressive sterilisation procedures'
      ],
      specifications: {
        type: 'Rigid sinuscope',
        diameters: '2.7 / 3 / 4 mm',
        configurations: 'Various working lengths & viewing directions'
      },
      images: ['/products/transparent/hd3-sinuskop-4-mm-0-web.png']
    },
    {
      id: 'laryngoscopes',
      name: 'Laryngoscopes',
      slug: 'laryngoscopes',
      tagline: 'Standard, zoom and paediatric laryngoscopes',
      description: 'The XION laryngoscope portfolio includes both standard laryngoscopes with fixed magnification and zoom laryngoscopes with two magnification levels. In both levels the control lever provides easy focussing. Paediatric laryngoscopes with a diameter of only 7 mm make the examination more comfortable for patients, particularly children and adolescents.',
      features: [
        'Standard laryngoscopes with fixed magnification',
        'Zoom laryngoscopes with two magnification levels',
        'Control lever for easy focussing in both levels',
        'Paediatric version with only 7 mm diameter',
        'More comfortable examinations for children and adolescents'
      ],
      specifications: {
        type: 'Rigid laryngoscope',
        versions: 'Standard · Zoom (two magnification levels)',
        paediatric: '7 mm diameter'
      },
      images: ['/products/transparent/zoom-laryngoskop-10-mm-70-web.png']
    },
    {
      id: 'laparoscopes',
      name: 'Laparoscopes',
      slug: 'laparoscopes',
      tagline: 'HD3-series laparoscopes for 4K/UHD surgery',
      description: 'XION endoscopes have been the basis of laparoscopic workplaces for years. The optical systems of the HD3 series offer the higher resolution and light intensity required for use with 4K/UHD camera systems, as well as a number of other improvements in detail.',
      features: [
        'HD3-series optical systems',
        'Higher resolution and light intensity for 4K/UHD camera systems',
        'Proven basis for laparoscopic workplaces',
        'Numerous detail improvements over previous generations',
        'Rugged construction for routine surgical use'
      ],
      specifications: {
        type: 'Rigid laparoscope',
        series: 'HD3',
        compatibility: '4K/UHD camera systems',
        diameter: '10 mm'
      },
      images: ['/products/transparent/hd3-laparoskop-10-mm-30-web.png']
    },
    {
      id: 'hysteroscopes',
      name: 'Hysteroscopes',
      slug: 'hysteroscopes',
      tagline: 'Rigid hysteroscopes for gynaecological endoscopy',
      description: 'XION hysteroscopes are available with diameters of 2.9 mm and 4 mm. An extensive range of different models of examination and surgical sheaths is available for the optics.',
      features: [
        'Diameters of 2.9 mm and 4 mm',
        'Extensive range of examination and surgical sheaths',
        'High-quality optics for gynaecological endoscopy',
        'Rugged and resilient to common reprocessing procedures'
      ],
      specifications: {
        type: 'Rigid hysteroscope',
        diameters: '2.9 / 4 mm',
        sheaths: 'Examination & surgical sheaths available'
      },
      images: ['/products/transparent/hd-hysteroskop-2-9-mm-30-web.png']
    },
    {
      id: 'xion-3d-endoscope',
      name: 'XION 3D Endoscope Attachments',
      slug: 'xion-3d-endoscope',
      tagline: 'True three-dimensional rigid endoscopy — a capability unique to XION',
      description: 'Various 3D optics attachments are available for different applications with the 3D camera head of the Spectar camera system. Two separate image channels and a unique calibration procedure enable excellent image quality even with small endoscope diameters from 2.7 mm. The simple, rugged coupling principle and the patented system design with the 3D sterile adapter also allow safe and convenient changing of optics under sterile conditions, as well as an extension of the entire system at moderate cost.',
      features: [
        'True 3D (stereoscopic) rigid endoscopy with the Spectar 3D camera head',
        'Two separate image channels with a unique calibration procedure',
        'Excellent image quality even at small diameters from 2.7 mm',
        'Simple, rugged coupling principle',
        'Patented 3D sterile adapter — change optics under sterile conditions',
        'Economic extension of an existing Spectar camera system to 3D'
      ],
      specifications: {
        type: '3D endoscope attachments',
        minDiameter: 'from 2.7 mm',
        compatibility: 'Spectar Camera Head 3D HD · MATRIX P/PL Spectar',
        adapter: 'Patented 3D sterile adapter'
      },
      images: ['/products/transparent/uebersicht-3d-endoskop-aufsaetze-web.png']
    }
  ]
};
